import { PricingItem } from '@/src/hooks/itinerary/usePriceCalculation'
import { TranslationFunction } from '@/src/types/translations'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Divider, Paper, Typography } from '@mui/material'

export interface PriceSummaryProps {
    pricingItems: PricingItem[]
    totalPrice: number
    travellerCount?: number
    formatCurrency: (price: number, usePrecision?: boolean) => string
    tPricing: TranslationFunction<'itineraryPage.pricing'>
    tCommon: TranslationFunction<'common'>
}

const PriceSummary: React.FC<PriceSummaryProps> = ({
    pricingItems,
    totalPrice,
    travellerCount = 1,
    formatCurrency,
    tPricing,
    tCommon
}) => {
    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                border: { xs: 'none', md: '1px solid' },
                borderColor: { xs: 'borders.default', md: 'borders.default' },
                p: 2
                // mb: 2
            }}
        >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight={600} fontSize={18}>
                    {tPricing('fareSummary')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {tCommon(travellerCount > 1 ? 'multiTravellers' : 'singleTraveller', {
                        travellers: travellerCount
                    })}
                </Typography>
            </Box>

            <Box>
                {pricingItems.map(item => (
                    <Box key={item.id} mb={1}>
                        {item.isCollapsible && item.subItems && item.subItems.length > 0 ? (
                            <Box
                                component="details"
                                open={travellerCount === 1 && item.id.startsWith('Traveller-') ? true : undefined}
                                sx={{
                                    '& summary': {
                                        listStyle: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        '&::-webkit-details-marker': {
                                            display: 'none'
                                        },
                                        '&::marker': {
                                            display: 'none'
                                        }
                                    },
                                    '& summary:hover': {
                                        xs: {},
                                        lg: {
                                            cursor: 'pointer',
                                            backgroundColor: 'action.hover',
                                            borderRadius: 1,
                                            px: 1,
                                            mx: -1
                                        }
                                    }
                                }}
                            >
                                <Box component="summary">
                                    <Box display="flex" alignItems="center">
                                        <Typography variant="body2" color="text.secondary" fontSize={14}>
                                            {item.label}
                                        </Typography>
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 0.5,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                transform: 'rotate(0deg)',
                                                transition: 'transform 0.2s',
                                                'details[open] > summary &': {
                                                    transform: 'rotate(180deg)'
                                                }
                                            }}
                                        >
                                            <ExpandMoreIcon sx={{ fontSize: 18 }} />
                                        </Box>
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        fontWeight={700}
                                        fontSize={14}
                                        color={item.isDiscount ? 'success.main' : 'text.primary'}
                                    >
                                        {item.isDiscount ? '-' : ''}
                                        {formatCurrency(item.value, item.usePrecision)}
                                    </Typography>
                                </Box>
                                <Box ml={2} mt={1}>
                                    {item.subItems.map(subItem => (
                                        <Box
                                            key={subItem.label}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            mb={0.5}
                                        >
                                            <Typography variant="body2" color="text.secondary" fontSize={14}>
                                                {subItem.label}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" fontSize={14}>
                                                {formatCurrency(subItem.value, item.usePrecision)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ) : (
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box display="flex" alignItems="center">
                                    <Typography variant="body2" color="text.secondary" fontSize={14}>
                                        {item.label}
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="body2"
                                    fontWeight={700}
                                    fontSize={14}
                                    color={item.isDiscount ? 'success.main' : 'text.primary'}
                                >
                                    {item.isDiscount ? '-' : ''}
                                    {formatCurrency(item.value, item.usePrecision)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle1" fontWeight={600}>
                    {tPricing('total')}
                </Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                    {formatCurrency(totalPrice)}
                </Typography>
            </Box>
        </Paper>
    )
}

export default PriceSummary
